type SearchableProduct = {
  name: string;
  shortDescription?: string;
  category: string;
};

const CATEGORY_LABELS: Record<string, string> = {
  household: "Household",
  cooker: "Cookers & Pressure Cookers",
  "kitchen essentials": "Kitchen Essentials",
  "hot pots": "Hot Pots",
  "mixer blender & grinders": "Mixers, Blenders & Grinders",
  "mixer blender and grinders": "Mixers, Blenders & Grinders",
  "bottles & flasks": "Bottles & Flasks",
  "bottles and flasks": "Bottles & Flasks",
  "stoves & burners": "Stoves & Burners",
  "stoves and burners": "Stoves & Burners",
  "restaurant supplies": "Restaurant Supplies",
  uncategorized: "Other Products",
};

const SEARCH_ALIASES: Record<string, string[]> = {
  airfryer: ["air fryer"],
  bottle: ["flask", "thermos"],
  box: ["container", "packaging", "takeaway"],
  cookware: ["cooker", "pot", "pan", "casserole", "chatti", "tawa", "utensil"],
  cooker: ["pressure cooker", "rice cooker", "pot"],
  cup: ["glass", "tumbler", "disposable"],
  flask: ["bottle", "thermos"],
  grinder: ["mixer", "blender"],
  household: ["storage", "cleaning", "home"],
  mixer: ["blender", "grinder"],
  pan: ["frying", "skillet", "tawa", "appachatty", "appachatti"],
  plate: ["dish", "dinnerware", "disposable"],
  pot: ["casserole", "stockpot", "hot pot", "chatti", "uruli"],
  stove: ["burner", "cooker", "gas"],
  storage: ["container", "drum", "bin", "box"],
  utensil: ["tool", "spoon", "ladle", "knife", "turner"],
};

const SEARCH_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "commercial",
  "for",
  "machine",
  "of",
  "product",
  "the",
  "with",
]);

const normalizeText = (value: string) =>
  value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");

const stem = (word: string) => {
  if (word.length > 4 && word.endsWith("ies")) return `${word.slice(0, -3)}y`;
  if (word.length > 4 && word.endsWith("es")) return word.slice(0, -2);
  if (word.length > 3 && word.endsWith("s") && !word.endsWith("ss"))
    return word.slice(0, -1);
  return word;
};

const editDistance = (left: string, right: string) => {
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let row = 1; row <= left.length; row += 1) {
    let diagonal = previous[0];
    previous[0] = row;
    for (let column = 1; column <= right.length; column += 1) {
      const above = previous[column];
      previous[column] = Math.min(
        previous[column] + 1,
        previous[column - 1] + 1,
        diagonal + (left[row - 1] === right[column - 1] ? 0 : 1),
      );
      diagonal = above;
    }
  }
  return previous[right.length];
};

const tokenMatches = (queryToken: string, productTokens: string[]) => {
  const queryStem = stem(queryToken);
  const tolerance = queryStem.length >= 7 ? 2 : queryStem.length >= 4 ? 1 : 0;
  return productTokens.some((candidate) => {
    const candidateStem = stem(candidate);
    if (
      candidateStem === queryStem ||
      (queryStem.length >= 3 && candidateStem.includes(queryStem)) ||
      (candidateStem.length >= 3 && queryStem.includes(candidateStem))
    ) {
      return true;
    }
    return tolerance > 0 && editDistance(queryStem, candidateStem) <= tolerance;
  });
};

export const formatCategory = (value?: string) => {
  const clean = normalizeText(value || "uncategorized");
  if (CATEGORY_LABELS[clean]) return CATEGORY_LABELS[clean];
  return clean
    .split(" ")
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");
};

export const formatProductName = (value: string) => {
  const clean = value
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\bhascehver\b/gi, "Hascevher")
    .replace(/\bappachatty\b/gi, "appachatti")
    .replace(/\s+([,.;:])/g, "$1");
  return clean ? `${clean.charAt(0).toUpperCase()}${clean.slice(1)}` : clean;
};

export const formatProductDescription = (value?: string) =>
  (value || "")
    .replace(/\r?\n+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\s+([,.;:])/g, "$1")
    .trim();

export const slugify = (value: string) =>
  normalizeText(value).replace(/\s+/g, "-").replace(/^-+|-+$/g, "");

export const categorySlug = (value: string) => slugify(formatCategory(value));

export const categoryPath = (value: string) =>
  `/products/category/${categorySlug(value)}`;

export const productSlug = (product: { id: string; name: string }) =>
  `${slugify(formatProductName(product.name))}-${product.id}`;

export const productPath = (product: { id: string; name: string }) =>
  `/products/${productSlug(product)}`;

const UUID_AT_END = /([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i;

export const extractProductId = (value: string) =>
  value.match(UUID_AT_END)?.[1] || value;

export const productMatchesSearch = (
  product: SearchableProduct,
  rawQuery: string,
) => {
  const query = normalizeText(rawQuery);
  if (!query) return true;

  const searchable = normalizeText(
    `${product.name} ${product.shortDescription || ""} ${product.category} ${formatCategory(product.category)}`,
  );
  if (searchable.includes(query)) return true;

  const productTokens = searchable.split(" ");
  const queryTokens = query
    .split(" ")
    .filter((token) => !SEARCH_STOP_WORDS.has(token));

  return queryTokens.every((queryToken) => {
    if (tokenMatches(queryToken, productTokens)) return true;
    return (SEARCH_ALIASES[stem(queryToken)] || []).some((alias) =>
      searchable.includes(normalizeText(alias)),
    );
  });
};
